// ================= THE MECHANICS DICTIONARY =================
//
// ONE SOURCE FOR WHAT PUNCH, CHAIN AND ABSORB MEAN, everywhere a player can read them: the compact
// card, the commit panels, the resolution beats, the exchange history, the library, the deck
// builder, and the in-bout glossary sheet. The brief that commissioned this file
// (sparring-cards-mechanics-visibility-implementation-brief.pdf, and the plan derived from it at
// deploy_handoff/mechanics-visibility-plan.md) states the rule this file exists to enforce:
//
//     "The UI copy must come from one centralized mechanics dictionary. Do not reproduce the
//      definitions separately in card, tutorial, resolution, library, and glossary components."
//
// WHY THE GAME NEEDED THIS. The engine has always resolved Attack Types (Punch/Kick/Grapple),
// follow-through (Chain/Impact/Control) and Defense Types (Absorb/Deny/Dodge/Deflect) correctly,
// and has always emitted the facts -- `aftermath.resolved` carries every one of them. None of it
// reached the screen. The labels appeared as uppercase tokens with their definitions hidden in
// `title=` attributes, which on a phone is no disclosure at all, and the consequences appeared only
// behind "Show exact log". The designer of the system reported forgetting his own defense families.
// That is the whole diagnosis: the game taught the output and not the system.
//
// SENTENCES ARE COMPOSED FROM STRUCTURED DATA, NEVER FROM THE AUTHORED BADGE STRINGS. Every card in
// js/cards-v4.js carries an `attackBadge`/`defenseBadge` one-liner, and reusing them was the obvious
// shortcut. It is wrong: 74 of them say a Combination Opening is "Punch-only", which this engine
// deliberately does not do (js/engine.js, COMBINATION_OPENING_IS_PUNCH_ONLY = false, whose own
// comment lists those 74 strings as known-stale residue). A generated file we do not edit cannot be
// the source of rules copy. So the sentences below are built from `v4.continuation` and
// `v4.defenseResult` -- the same structured fields the engine reads -- and a rules change moves the
// text with it or fails a test. There is no path by which this file can describe a rule the engine
// is not running.
//
// NO DEPENDENCIES BY DESIGN. library.html and deck-builder.html are not the game shell and cannot
// load js/ui.js; this file is what lets them show the same definitions. It reads `window.CARDS`
// only through the card objects handed to it, so it can be loaded anywhere and tested standalone.
//
// Loads after js/cards-v4.js and js/cards-season-summer.js (it reads the `v4` block those attach)
// and before js/card-tile.js, which renders its badges.
(function () {
  'use strict';

  function escapeHtml(str) {
    return String(str).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  }

  // ---------------- Icons ----------------
  //
  // SHAPE CARRIES THE MEANING AND COLOUR ONLY REINFORCES IT. The brief's accessibility criterion is
  // explicit -- "icons have text labels and do not rely on color alone" -- and these badges sit at
  // 10px on a phone where two hues are not reliably distinguishable anyway. Every glyph below is
  // distinguishable in a single ink.
  //
  // Same idiom as the Style sigils in js/card-tile.js: 24x24 viewBox, `currentColor`, no fills that
  // would go invisible against a dark panel. Kept deliberately simple -- at badge size a detailed
  // glyph is a smudge, and these are read as marks, not pictures.
  const ICONS = {
    // Chamber (RULES.md §19): a coil under tension feeding a straight line out of it, drawn
    // mid-spring so it reads as stored force rather than as a decorative spiral. ONE glyph for both
    // halves of the mechanic — loading and releasing are the same coil at two moments, and two
    // icons would ask a player to learn that they are one idea.
    chamber: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 20c4-2 4-4.5 0-6.5S2 9 6 7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M11 12h9" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M16 8l4 4-4 4" stroke="currentColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/></svg>',
    // Reshuffle fatigue (RULES.md §2): a DASHED card frame -- the deck that is no longer there --
    // and a loop arrow returning into it. Nothing else in this table is a rectangle, so it reads
    // as a card rather than as another shield or another strike even at badge size.
    fatigue: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="2.5" y="4.5" width="10" height="15" rx="2" stroke="currentColor" stroke-width="2" stroke-dasharray="3 2.6"/><path d="M17 8.5a5 5 0 1 1-1.7 6.4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M21.5 7v4h-4" stroke="currentColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/></svg>',
    // Cycling (RULES.md §2): a card with an arrow leaving it. DELIBERATELY THE SAME RECTANGLE the
    // fatigue glyph draws, because the two are one system -- cycling is the act, reshuffle fatigue
    // is its price -- and the difference between them is the direction of the arrow. Fatigue loops
    // back into an empty frame; this one points out of a full one.
    cycle: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="2.5" y="4.5" width="9" height="15" rx="2" stroke="currentColor" stroke-width="2"/><path d="M15 12h6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M18 9l3 3-3 3" stroke="currentColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/></svg>',
    // Attack Types. A straight line for the hand, an arc for the leg, interlocking links for
    // the body -- the three are different at a glance even out of focus.
    punch: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 12h11" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M14 7l5 5-5 5" stroke="currentColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/></svg>',
    kick: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 20C4 10 10 4 20 4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M15 4h5v5" stroke="currentColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/></svg>',
    grapple: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="9" cy="12" r="5" stroke="currentColor" stroke-width="2"/><circle cx="15" cy="12" r="5" stroke="currentColor" stroke-width="2"/></svg>',
    knee: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 21V9l7 6" stroke="currentColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/><path d="M13 15l5-9" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
    elbow: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 5l8 8-8 6" stroke="currentColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/><path d="M12 13h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
    // Follow-through. Links for a chain, a starburst for impact, a bracket that closes around
    // something for control.
    chain: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="2.5" y="8.5" width="9" height="7" rx="3.5" stroke="currentColor" stroke-width="2"/><rect x="12.5" y="8.5" width="9" height="7" rx="3.5" stroke="currentColor" stroke-width="2"/></svg>',
    impact: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 2v5M12 17v5M2 12h5M17 12h5M5 5l3.5 3.5M15.5 15.5L19 19M19 5l-3.5 3.5M8.5 15.5L5 19" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
    control: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M8 3H4v18h4" stroke="currentColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/><path d="M16 3h4v18h-4" stroke="currentColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/><circle cx="12" cy="12" r="2" fill="currentColor"/></svg>',
    authored: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 5h14M5 12h14M5 19h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
    // Defense Types. All four are a shield so the family reads as one family, and each is cut
    // differently: solid, sliced, barred, dashed-and-offset.
    absorb: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 2l8 3v7c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V5z" fill="currentColor"/></svg>',
    deflect: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 2l8 3v7c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V5z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><path d="M6 14L18 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
    deny: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 2l8 3v7c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V5z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><path d="M5 11h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
    dodge: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M14 3l7 2.5v6c0 4.4-3 7.5-7 8.8" stroke="currentColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" stroke-dasharray="3 2.5"/><path d="M9 4.5L4 8l5 3.5" stroke="currentColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/></svg>',
    // The track. Two crests, because the thing being drawn is a level that rises and falls rather
    // than a technique -- and the same two waves the Cleanup chip has carried since the Deep Flow
    // surfaces shipped, so the badge a player already recognises does not change under them.
    flow: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 9c3-3 6-3 9 0s6 3 9 0M3 16c3-3 6-3 9 0s6 3 9 0" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
    // Range fit. One family of three, and the only one here that is a PICTURE OF A MEASUREMENT
    // rather than of a technique: the same three range bands every time, the filled one being where
    // the card wants to be, and an arrow dropping onto where the exchange actually happened. The
    // distance between them is the whole rule, so the icons differ by exactly that distance --
    // nought, one band, two.
    fitOptimal: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="5" cy="17" r="2" stroke="currentColor" stroke-width="2"/><circle cx="12" cy="17" r="2" fill="currentColor"/><circle cx="19" cy="17" r="2" stroke="currentColor" stroke-width="2"/><path d="M12 3v6M9 6.5l3 3 3-3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    fitCompromised: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="5" cy="17" r="2" stroke="currentColor" stroke-width="2"/><circle cx="12" cy="17" r="2" fill="currentColor"/><circle cx="19" cy="17" r="2" stroke="currentColor" stroke-width="2"/><path d="M19 3v6M16 6.5l3 3 3-3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    fitWhiff: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="5" cy="17" r="2" fill="currentColor"/><circle cx="12" cy="17" r="2" stroke="currentColor" stroke-width="2"/><circle cx="19" cy="17" r="2" stroke="currentColor" stroke-width="2"/><path d="M19 3v6M16 6.5l3 3 3-3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    // Positioning Intent. Not a card mechanic -- the three are chosen beside the cards and printed
    // on none of them -- but they are read in exactly the places everything else here is read: the
    // commit strip picks one, the Footwork beat resolves it, and the exchange history now keeps it.
    // A glyph drawn in two files is a glyph that drifts, and these had been drawn in js/ui.js
    // alone. Moved here unchanged, so the mark on the button a player pressed is the mark the rail
    // prints back at them an exchange later.
    //
    // The three are a picture of what the Intent does to the distance -- an arrow driving into a
    // wall, a fixed post, an arrow retreating from one -- so direction alone separates Pressure
    // from Evade, which is what has to survive at the 12px they run at in a history row.
    intentPressure: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 12h11" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M12 7l5 5-5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M20 5v14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
    intentAnchor: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 7v13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="12" cy="4.5" r="2.2" stroke="currentColor" stroke-width="2"/><path d="M5 14a7 7 0 0 0 14 0" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
    intentEvade: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M20 12H9" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M12 7l-5 5 5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M4 5v14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>'
  };

  // Addressed by the engine's own names (window.INTENTS), and '' for anything else -- so a caller
  // can pass an Intent straight out of a position.resolved event without first checking it is one.
  const INTENT_ICONS = {
    Pressure: ICONS.intentPressure, Anchor: ICONS.intentAnchor, Evade: ICONS.intentEvade
  };
  function intentMark(intent) { return INTENT_ICONS[intent] || ''; }

  // ---------------- The three namespaces ----------------
  //
  // `key` is the value that appears in card data, so a lookup is a plain index and never a mapping
  // table that can drift. `label` is what the badge prints; `shortLabel` is what it prints at 360px,
  // where the full word does not fit beside the ATK/DEF key.
  //
  // THE FOUR COPY FIELDS ARE FOUR DIFFERENT JOBS and are not interchangeable:
  //   trigger  -- WHEN the rule is evaluated. The single most-missed fact: three of the four Defense
  //               Types do nothing at all unless the guard was full, which is why a player who
  //               chooses Deny for its range control and gets partially hit sees no range control.
  //   oneLiner -- the badge tooltip and the commit-panel line. One sentence, complete on its own.
  //   long     -- the glossary paragraph, with the rule stated as RULES.md states it.
  //   sample   -- a worked example. The glossary sheet shows it under `long`; abstract rule text is
  //               what players skim past.

  const ATTACK_TYPES = {
    // Punch/Kick/Grapple come from `v4.family`, the ruleset's own coarse category. Knee and Elbow
    // have `v4.family: null` and are recovered from the engine's finer `attack.category` -- see
    // attackTypeOf. Without them 22 cards (11%) would show no Attack Type at all, and the brief's
    // acceptance criterion is that EVERY card's type is identifiable from the hand.
    Punch: {
      key: 'Punch', label: 'PUNCH', shortLabel: 'PUN', icon: ICONS.punch, colorToken: '--mech-punch',
      trigger: 'Whenever this card attacks.',
      oneLiner: 'Punch — a hand strike. Most Punches chain: contact leaves an Opening you can attack into next exchange.',
      long: 'Punch is the ruleset\'s category for hand strikes. It is what conditional card text and ' +
            'Style passives match on when they name a type, and it is usually paired with the Chain ' +
            'follow-through. Punches are the fastest family and the narrowest: most are Optimal in ' +
            'only one range band, so a Clash that drifts is what stops them.',
      sample: 'Stiff Jab is a Punch. Landing it chains to Mid, so next exchange a Mid attack gets +1 Power.'
    },
    Kick: {
      key: 'Kick', label: 'KICK', shortLabel: 'KCK', icon: ICONS.kick, colorToken: '--mech-kick',
      trigger: 'Whenever this card attacks.',
      oneLiner: 'Kick — a leg strike. Most Kicks carry Impact: they cost the defender Stamina even through a full guard.',
      long: 'Kick is the ruleset\'s category for leg strikes. It reaches further than a Punch and is ' +
            'usually paired with the Impact / Recovery follow-through, which is the only follow-through ' +
            'that gets through an Absorb. The trade is the recovery: a Kick that is Deflected or Dodged ' +
            'leaves its thrower exposed at a printed level.',
      sample: 'A Kick that is fully guarded by an Absorb still costs the defender 1 Stamina, and cancels their recovery.'
    },
    Grapple: {
      key: 'Grapple', label: 'GRAPPLE', shortLabel: 'GRP', icon: ICONS.grapple, colorToken: '--mech-grapple',
      trigger: 'Whenever this card attacks.',
      oneLiner: 'Grapple — a takedown, throw or sweep. Most Grapples claim the range: contact decides where the exchange ends.',
      // THE LAST SENTENCE USED TO DESCRIBE THE TEXT CONVENTION AND WAS READ AS THE MATCHING RULE.
      // It said a card's Conditions text names its printed technique -- true of how the texts were
      // written, and the reader took it to mean a "vs Takedowns" defense answers takedowns only.
      // Nothing in the repo conditions on traits: every one of those hooks matches
      // `category === 'Grapple'`, so five defenses were quietly better than they read. The 2026-08-21
      // review found the gap; the card texts were rewritten to say Grapple and this sentence now
      // states the rule they follow rather than the convention they used to.
      long: 'Grapple is the ruleset\'s category for body control — takedowns, throws and sweeps. It is ' +
            'usually paired with the Control follow-through, which applies the card\'s printed range ' +
            'result on contact. A conditional that names grappling matches the whole category: a ' +
            'defense reading "against Grapples" answers a throw, a sweep and a takedown alike, ' +
            'because the category is the rule and the technique is flavour.',
      sample: 'A Grapple with Control: setPocket ends the exchange at Pocket whether or not the opponent wanted it there.'
    },
    // §11 is explicit that a named knee or elbow is NOT a Kick or a Punch for a conditional check.
    // That is a rule a player can only learn from being told, so both entries say it outright.
    Knee: {
      key: 'Knee', label: 'KNEE', shortLabel: 'KNE', icon: ICONS.knee, colorToken: '--mech-knee',
      trigger: 'Whenever this card attacks.',
      oneLiner: 'Knee — a named knee strike. It is not a Kick for card text that names a type.',
      long: 'A named Knee is its own category. Close-range and heavy, it carries a follow-through like ' +
            'any other attack, but a card that says "against a Kick" does not match it — the ruleset ' +
            'treats named knees and elbows as distinct from the three main types.',
      sample: 'A Defense with a Guard bonus against Kicks gets no bonus against Clinch Knee.'
    },
    Elbow: {
      key: 'Elbow', label: 'ELBOW', shortLabel: 'ELB', icon: ICONS.elbow, colorToken: '--mech-elbow',
      trigger: 'Whenever this card attacks.',
      oneLiner: 'Elbow — a named elbow strike. It is not a Punch for card text that names a type.',
      long: 'A named Elbow is its own category. Short, sharp and usually Optimal only in close, it ' +
            'carries a follow-through like any other attack, but a card that says "against a Punch" ' +
            'does not match it — the ruleset treats named knees and elbows as distinct from the three ' +
            'main types.',
      sample: 'A Defense with a Guard bonus against Punches gets no bonus against Horizontal Elbow.'
    }
  };

  const FOLLOW_THROUGH = {
    Chain: {
      key: 'Chain', label: 'CHAIN', shortLabel: 'CHN', icon: ICONS.chain, colorToken: '--mech-chain',
      trigger: 'On contact — the attack landed, or was Absorbed.',
      oneLiner: 'Chain — contact creates the printed Opening for the next exchange.',
      long: 'A Chain converts a landed attack into position. On contact the card places its printed ' +
            'Opening on the defender at the level it names, worth +1 Power to whoever attacks that ' +
            'level next exchange. The Opening lasts one exchange only and any attack can spend it.',
      // The house rule, stated where a player will actually meet it. The archive's own badge text
      // still says Punch-only; this engine does not (js/engine.js COMBINATION_OPENING_IS_PUNCH_ONLY).
      sample: 'A Chain to Mid means any attack aimed Mid next exchange gets +1 Power — not only a Punch.'
    },
    ImpactRecovery: {
      key: 'ImpactRecovery', label: 'IMPACT', shortLabel: 'IMP', icon: ICONS.impact, colorToken: '--mech-impact',
      trigger: 'On contact for the Impact; on a Deflect or Dodge for the Recovery.',
      oneLiner: 'Impact / Recovery — it costs Stamina even through an Absorb, but a Deflect or Dodge leaves you exposed.',
      long: 'The only follow-through that pays through a full guard. Where the defender Absorbs, the ' +
            'attack still costs them 1 Stamina and cancels the Absorb\'s own recovery. The price is ' +
            'symmetrical: if the defender Deflects or Dodges instead, the attacker is caught on the ' +
            'recovery and takes a printed Opening of their own.',
      sample: 'An Impact kick into an Absorb: the defender loses 1 Stamina and recovers nothing.'
    },
    Control: {
      key: 'Control', label: 'CONTROL', shortLabel: 'CTL', icon: ICONS.control, colorToken: '--mech-control',
      trigger: 'On contact — the attack landed, or was Absorbed.',
      oneLiner: 'Control — contact applies this card\'s printed range result, so the attacker decides where the exchange ends.',
      long: 'Control turns a landed Grapple into position rather than into an Opening. On contact the ' +
            'card\'s printed range result is applied to the End of the exchange — closing a band, ' +
            'setting a specific range, or holding wherever the Clash happened. Where two range claims ' +
            'conflict, the End returns to the Clash range.',
      sample: 'A Control: setClinch drags the exchange to Clinch on contact, whatever the opponent intended.'
    },
    // Three keys, one presentation. The archive distinguishes AuthoredSetupMap / AuthoredSetup /
    // Authored; to a player they are the same statement -- "the rule is on the card" -- and giving
    // them three different badges would teach a distinction that changes nothing they can act on.
    AuthoredSetupMap: null,   // filled below
    AuthoredSetup: null,
    Authored: {
      key: 'Authored', label: 'AUTHORED', shortLabel: 'AUT', icon: ICONS.authored, colorToken: '--mech-authored',
      trigger: 'As printed in this card\'s Conditions.',
      oneLiner: 'Authored — this card prints its own follow-up instead of using a standard one.',
      long: 'A handful of cards do not take one of the three standard follow-throughs. What happens ' +
            'after contact is written in the card\'s own Conditions text, which is the only statement ' +
            'of it — the type badge is telling you to read the card rather than to expect a Chain, an ' +
            'Impact or a Control.',
      sample: 'Read the card\'s Conditions: whatever it says there is the whole of its follow-through.'
    }
  };
  FOLLOW_THROUGH.AuthoredSetupMap = FOLLOW_THROUGH.Authored;
  FOLLOW_THROUGH.AuthoredSetup = FOLLOW_THROUGH.Authored;

  const DEFENSE_TYPES = {
    // ALL FOUR TRIGGER ON A FULL GUARD AND NOTHING ELSE (§13, and js/engine.js's buildAftermathClaim
    // sets `fam` to null unless `result.fullGuard`). This is the single most-missed fact in the game:
    // a player picks Deny for its range control, gets partially hit, and sees no range control --
    // correctly, and with nothing on screen ever having said why. Every `trigger` below says it.
    Absorb: {
      key: 'Absorb', label: 'ABSORB', shortLabel: 'ABS', icon: ICONS.absorb, colorToken: '--mech-absorb',
      trigger: 'On a Full Guard — your Defense covered every level the attack reached.',
      oneLiner: 'Absorb — a full guard counts as Contact and recovers 1 Stamina, if the attack cost you none.',
      long: 'Absorb is the only Defense Type that still counts as Contact, which means the attack\'s ' +
            'follow-through happens anyway: an absorbed Punch still Chains, an absorbed Grapple still ' +
            'claims its Control. What you get for it is Stamina — 1 recovered, provided the attack ' +
            'took none off you. Recovery is capped at 2 per exchange across every source, and a ' +
            'fighter at 0 Stamina cannot recover at all.',
      sample: 'Absorb a jab for no damage: you recover 1 Stamina, and the jab still chains.'
    },
    Deflect: {
      key: 'Deflect', label: 'DEFLECT', shortLabel: 'DFL', icon: ICONS.deflect, colorToken: '--mech-deflect',
      trigger: 'On a Full Guard — your Defense covered every level the attack reached.',
      oneLiner: 'Deflect — a full guard cancels the attack\'s follow-through and leaves the attacker open.',
      long: 'Deflect answers an attack by breaking its continuation and turning the attacker\'s own ' +
            'commitment against them: they take a +1 Opening at the level they aimed at, or at the ' +
            'level their card prints for a recovery. Against a Grapple\'s Control it holds the End at ' +
            'the Clash range rather than letting the attacker set it.',
      sample: 'Deflect a Mid punch: its Chain is cancelled and the puncher is open at Mid next exchange.'
    },
    Deny: {
      key: 'Deny', label: 'DENY', shortLabel: 'DNY', icon: ICONS.deny, colorToken: '--mech-deny',
      trigger: 'On a Full Guard — your Defense covered every level the attack reached.',
      oneLiner: 'Deny — a full guard cancels the attack\'s follow-through and applies this card\'s printed range result.',
      long: 'Deny is the positional answer. It breaks the attack\'s continuation like a Deflect, but ' +
            'instead of opening the attacker it claims the range: the card\'s printed Control is ' +
            'applied to the End of the exchange. It is the most common Defense Type in the game and ' +
            'the one that most rewards knowing where you want the fight to be.',
      sample: 'Deny with a printed End at Pocket: the attack develops into nothing and the exchange ends at Pocket.'
    },
    Dodge: {
      key: 'Dodge', label: 'DODGE', shortLabel: 'DDG', icon: ICONS.dodge, colorToken: '--mech-dodge',
      trigger: 'On a Full Guard — your Defense covered every level the attack reached.',
      oneLiner: 'Dodge — a full guard turns the attack into a Whiff and moves the range the way this card prints.',
      long: 'Dodge is the cleanest refusal: the attack does not merely fail, it counts as a Whiff, so ' +
            'nothing that keys off contact happens at all. The card\'s printed range result then ' +
            'applies — usually a step out to a farther band, which is what makes Dodge the answer to ' +
            'pressure rather than to damage.',
      sample: 'Dodge a clinch entry: it whiffs outright and the exchange ends one band farther out.'
    }
  };

  // ---------------- A fourth namespace: how well the card fits the range ----------------
  //
  // WHY THIS ONE IS DIFFERENT FROM THE THREE ABOVE. Those are properties a card PRINTS — a Punch is
  // a Punch in every exchange it is ever played in. Range fit is a property of an exchange: the same
  // card is Optimal in one and does nothing at all in the next, and which it is was decided by an
  // Intent contest that happened after both players committed. So the key is not in card data; it
  // comes from ENGINE.rangeEffectiveness comparing the card's printed bands to the Clash Range.
  //
  // AND IT IS THE ONE PLAYERS ACTUALLY LOSE TO. §5's ladder decides most of what a first-time
  // player finds inexplicable — an attack that "hit" for two less than it says, or a card that did
  // nothing whatever with no message beyond one word — and until now the game printed that word and
  // stopped. The dictionary's own header names this failure mode: the game taught the output and not
  // the system.
  //
  // THE ONE-LINERS CARRY THE NUMBERS, deliberately and unlike the three namespaces above, which can
  // afford to be qualitative. These are what the fit pill's title and the coach line print, and
  // "-2 Power" and "no clean-hit effects" ARE the rule; a sentence that said "less effective" would
  // be the same non-explanation the bare word already gives.
  const RANGE_FIT = {
    Optimal: {
      key: 'Optimal', label: 'OPTIMAL', shortLabel: 'OPT', icon: ICONS.fitOptimal,
      colorToken: '--mech-optimal',
      trigger: 'When your attack resolves, if the Clash Range is one of its printed Optimal bands.',
      oneLiner: 'Optimal — the exchange happened where your card wants it. Full Power, and its ' +
                'clean-hit text can fire.',
      long: 'Every attack prints the range band or bands it is Optimal in, and the exchange is ' +
            'fought at the Clash Range — which is decided by the Positioning Intents, not by the ' +
            'cards. Optimal is the case where those agree. Nothing is deducted and nothing is ' +
            'switched off, which is why it is worth choosing an Intent for.',
      sample: 'A jab Optimal at Long and Pocket, in an exchange that clashes at Pocket: full Power, ' +
              'and the Opening it prints on a clean hit happens.'
    },
    Compromised: {
      key: 'Compromised', label: 'COMPROMISED', shortLabel: 'CMP', icon: ICONS.fitCompromised,
      colorToken: '--mech-compromised',
      trigger: 'When your attack resolves one band away from its nearest Optimal range.',
      oneLiner: 'Compromised — one band out of position: -2 Power, and no clean-hit effects fire ' +
                'at all.',
      long: 'Compromised is TWO penalties and the second one is the one that gets missed. The Power ' +
            'loss is visible in the damage; the card\'s printed "on a clean hit" text simply does ' +
            'not happen, even when the attack lands cleanly. An Opening you were counting on, a ' +
            'range claim, a Stamina drain — none of it fires. Power never goes below zero.',
      sample: 'A Clinch-Optimal knee thrown in an exchange that clashes at Pocket lands for 2 less ' +
              'and leaves no Opening behind, however cleanly it connects.'
    },
    Whiff: {
      key: 'Whiff', label: 'WHIFF', shortLabel: 'WHF', icon: ICONS.fitWhiff,
      colorToken: '--mech-whiff',
      trigger: 'When your attack resolves two bands away from its nearest Optimal range.',
      oneLiner: 'Whiff — two bands out of position: the attack does nothing. There is no Guard ' +
                'check at all.',
      long: 'At two bands the attack is not weakened, it is absent — the defender does not even ' +
            'roll their guard against it, so nothing that keys off being attacked happens either. ' +
            'Long and Clinch are two bands apart, so this is what a Long-only attack does in a ' +
            'clinch, and it is the single most expensive thing an Intent can cost you.',
      sample: 'A Long-only kick in an exchange that clashes at Clinch: nothing happens, and the ' +
              'Stamina you spent on it is spent.'
    }
  };

  // ---------------- The track ----------------
  //
  // THE ONE CORE SYSTEM WITH NOTHING BEHIND IT. Until this entry existed a grep for "Flow" or
  // "Fury" in this file returned nothing at all, which meant js/mechanics-coach.js could never
  // teach the track: the coach can only claim a dictionary key. Meanwhile the very first bout
  // surfaces it three ways -- the Zone 1 meter is labelled CALM ... FURY and nothing else, a
  // first-exchange card says "shift Flow -1", and a Foundation passive announces itself moving the
  // thing. The 2026-08-21 review sampled five first bouts: every one moved the track and not one
  // could explain it.
  //
  // ONE ENTRY FOR BOTH ENDS, not two. They are one axis with one number on it, and a player who
  // has met Deep Flow needs to learn where Fury is from the same place -- splitting them would put
  // the asymmetry (the whole point) in neither entry. The label is the track; the BAND names still
  // appear where a band is what happened, which is why a Cleanup row reads "DEEP FLOW -> RECOVER 1"
  // under a FURY-FLOW badge.
  //
  // The shortLabel and colour are inherited rather than chosen: js/ui-resolution.js has been
  // drawing FLW in recovery-green on the Cleanup recap since the Deep Flow surfaces landed, and a
  // glossary entry that renamed or recoloured it would break the one continuity a player has.
  //
  // ONE UPPERCASE WORD FOR THE LABEL, which is this table's own rule and not a preference: the
  // suite asserts /^[A-Z]+$/ on every label because it is a ~10px badge, and openSheet builds the
  // sheet title by lowercasing everything after the first character -- "FURY–FLOW" would have
  // titled the sheet "Fury–flow". ImpactRecovery set the precedent for a compound concept: its key
  // is the pair and its label is the leading word. FLOW is the half this entry's rows are about
  // (every one of them is a recovery row) and the half FLW already abbreviates; the Fury end is
  // named in the first sentence of the one-liner, which is what the sheet opens with.
  const TRACK = {
    FuryFlow: {
      key: 'FuryFlow', label: 'FLOW', shortLabel: 'FLW', icon: ICONS.flow,
      colorToken: '--mech-deepflow',
      trigger: 'Whenever the track crosses -2 or +2. Cards and Foundation passives move it; ' +
               'nothing decays it on its own.',
      oneLiner: 'One track from -3 to +3. At Fury (+2 or higher) your Attack gains +1 Power, ' +
                'unconditionally. At Deep Flow (-2 or lower) you recover 1 Stamina during ' +
                'Aftermath — but only if the opponent\'s Attack did not damage you.',
      long: 'This is the game\'s one persistent mindset state, and its two ends are deliberately ' +
            'not symmetrical. Fury pays whatever happens. Flow pays only while you are not being ' +
            'hit, which is the counter-play that end has: hitting a fighter who is sitting in Flow ' +
            'is what takes it away from them. Flow\'s recovery also draws on the same cap of 2 per ' +
            'fighter per exchange that Absorb and card effects draw on, so a busy exchange can ' +
            'spend the budget before the track is even evaluated. Being at high Fury does not ' +
            'expose you — it never counts as Off-Balance and creates no vulnerability by itself. ' +
            'Card text always names the COORDINATE, not the band: "Requires Flow +2" means the ' +
            'number on this track, and the band that number sits in is Fury.',
      sample: 'A fighter at Flow -3 who fully guards recovers 1 Stamina in Aftermath. The same ' +
              'fighter taking 2 damage that exchange recovers nothing — they lose the Stamina and ' +
              'the recovery both.'
    }
  };

  // CHAMBER (RULES.md §19). Its own table rather than an entry in one of the five above, because it
  // is not a property of an Attack, a Defense, a range or the Fury–Flow track — it is a resource a
  // fighter holds, and the two card tags that touch it are the only things in the game that mention
  // it. rules.html renders every entry here through `sheetBodyHtml`, so this one object is the
  // in-bout sheet AND the reference page, and the two cannot come to word the rule differently.
  const CHAMBER = {
    Chamber: {
      key: 'Chamber', label: 'CHAMBER', shortLabel: 'CHM', icon: ICONS.chamber,
      colorToken: '--mech-chamber',
      trigger: 'Only from printed card text. A deck with no Chamber card never sees it.',
      oneLiner: 'Chamber (max 2) — cards build it, a real hit clears it, and a Release Attack ' +
                'spends all of it: +1 Power for one, +3 for two.',
      long: 'Stored preparation. Certain Kinetic Impact cards load a Chamber when their work goes ' +
            'unrewarded: a feint the opponent successfully stopped, a frame that caught a real ' +
            'shot cleanly. It does nothing by itself and it does not expire — it simply waits, up ' +
            'to two, until you spend it or they take it away. Taking it away means one Attack of ' +
            'theirs costing you 2 or more Stamina; a single point does not disturb it, and neither ' +
            'does a Stamina cost of your own, reshuffle fatigue, or an Attack you stopped dead. ' +
            'Spending it is a Release Attack, and the decision is made at Commit, before you see ' +
            'their Defense or their Intent: an armed Release spends every Chamber you hold, and ' +
            'the second one is worth more than the first — +1 Power for one, +3 for two. It spends ' +
            'whether it lands or not.',
      sample: 'You hold 2 Chamber and arm Front Snap Kick. It resolves at Power 5 instead of 2 — ' +
              'unless their faster Attack got through for 2 or more first, in which case there was ' +
              'nothing left to spend and the arm cost you nothing but the plan.'
    }
  };

  // CYCLING (RULES.md §2, §16.5). Its own table beside Fatigue's, and the pair is the argument for
  // both being here: cycling is a thing a player DOES and fatigue is what it eventually costs, so
  // they are two entries in one system rather than one entry with a long paragraph. A reader who
  // taps the CYC badge on a card they are about to throw wants the rule they are using; a reader
  // who taps FTG on a Stamina that just vanished wants the rule that took it.
  //
  // THEY SHARE --mech-fatigue on purpose, which is the only place in this table two entries share
  // a colour. The Select screen paints a cycling card in that amber and the warning under the tray
  // in the same amber, because they are the same decision seen at two moments -- a third hue would
  // teach a player that they are two unrelated things.
  const CYCLE = {
    Cycle: {
      key: 'Cycle', label: 'CYCLE', shortLabel: 'CYC', icon: ICONS.cycle,
      colorToken: '--mech-fatigue',
      trigger: 'When you pick your two cards for an exchange. Any card you did not pick may be ' +
               'marked; nothing forces you to mark any.',
      oneLiner: 'Throw away any number of the cards you did not pick, and refill to 5 at the end ' +
                'of the exchange. Your hand size never changes — this buys depth, not cards.',
      long: 'The deck is 30 cards and a bout is at most 10 exchanges, so without this most of ' +
            'what you built is never dealt to you. Cycling is how you go looking for the card ' +
            'your plan needs instead of waiting to be handed it. The marked cards are discarded ' +
            'at the same moment your two played cards are, and you draw back to five — cycling ' +
            'two means drawing four. There is no separate cost, because there is already one: ' +
            'every extra card you draw moves you toward the bottom of your deck, and reaching ' +
            'the bottom reshuffles it and costs you 1 Stamina. Cycle freely, but you are burning ' +
            'the tank and you pay for it later. The marks are secret until the cards are ' +
            'discarded; after that they are in a pile both players can read.',
      sample: 'The fight is in the Clinch and you are holding two long-range kicks. Mark both, ' +
              'play the pair you have, and draw four cards instead of two — with 12 left in the ' +
              'deck, that costs you nothing at all.'
    }
  };

  // RESHUFFLE FATIGUE (RULES.md §2). Its own table for Chamber's reason: it is not a property of an
  // Attack, a Defense, a range or the Fury–Flow track. It is the deck itself, and it is the only
  // entry here that describes something no card causes and no opponent chooses.
  //
  // WHY IT EXISTS NOW. The rule has been printed since the first RULES.md and the Stamina has been
  // deducted correctly all along, but nothing anywhere said so: js/engine.js drawCard took the
  // point with no log line and no event, so the bout log — the surface whose job is mechanical
  // traceability — had no sentence about it, and classifyBoutEnd had no crossing to attribute a
  // fatigue stoppage to. That was survivable only because 5 + 2x10 draws never reach the bottom of
  // 30 cards; it fired on 0.0% of 7,500 measured fighter-bouts. A rule a player is invited to plan
  // around should not be a rule they can only discover by losing to it.
  const FATIGUE = {
    Fatigue: {
      key: 'Fatigue', label: 'FATIGUE', shortLabel: 'FTG', icon: ICONS.fatigue,
      colorToken: '--mech-fatigue',
      trigger: 'When you must draw and your deck is empty. Nothing else causes it — no card, no ' +
               'Attack, and nothing the opponent chooses.',
      oneLiner: 'Run out of deck and your discard is reshuffled into a new one — and you lose ' +
                '1 Stamina for it. This is the only clock in the game besides the 10-exchange limit.',
      long: 'A bout deals 5 cards and draws 2 more at the end of every exchange, so 30 cards ' +
            'comfortably outlast the 10-exchange limit and most fights never see this at all. It ' +
            'is reachable: any card that makes you draw out of turn moves you toward the bottom, ' +
            'and a fight that goes the distance after several of them can run you dry. The ' +
            'Stamina is taken the moment the reshuffle happens, mid-draw, and it is taken the ' +
            'same way a punch takes one — reaching 0 ends the bout immediately, so it can be the ' +
            'thing that finishes you. It is not damage, though: it breaks no Chamber, it triggers ' +
            'nothing that keys off being hit, and no opponent gets credit for it.',
      sample: 'You draw your last two cards to refill at the end of exchange 8. The discard is ' +
              'shuffled into a fresh deck and you drop from 3 Stamina to 2 — nobody touched you.'
    }
  };

  // ---- POSITIONING INTENT (RULES.md 4) --------------------------------------------------------
  //
  // THE LAST MECHANIC IN THE GAME WITH NO GLOSSARY ENTRY, and the one the commit screen is
  // actually built around. Every other table here explains something that HAPPENS TO you -- a
  // Defense Family, a range fit, a Chamber. The Intent contest is the only thing you choose
  // blind against somebody else choosing blind, and until now a player who lost one could read
  // that the range moved and nowhere read why that was a decision rather than an event.
  //
  // The 2026-08-29 engagement review measured what that costs: the whole simultaneous-reveal
  // channel is worth ~4-5 win-rate points to the strongest agent, against ~41 for card choice,
  // and the default opponent plays 80% noise -- so a player has neither a reason to study the
  // read nor an opponent whose read is legible. Fixing the second is a difficulty question. This
  // is the first: the vocabulary exists to be taught with.
  //
  // THREE ENTRIES, NOT ONE, matching how every other table here is keyed -- by the value a
  // surface actually holds. The coach names the intent that WON, so the sheet it opens is that
  // intent's own entry, and js/rules-content.js's cycle prose stays the place the triangle is
  // described as a whole.
  //
  // They share --mech-chamber's cool hue for the reason the range-fit entries share the pill
  // palette: positioning is not a blow, and inventing three more hues would teach a reader that
  // colour means nothing here.
  const INTENT = {
    Pressure: {
      key: 'Pressure', label: 'PRESSURE', shortLabel: 'PRS', icon: ICONS.intentPressure,
      colorToken: '--mech-chamber',
      trigger: 'Chosen at Commit, revealed with your opponent\'s. Beats Evade.',
      oneLiner: 'Pressure closes the distance a band, and beats Evade in the contest.',
      long: 'The three Intents are a closed cycle: Pressure beats Evade, Evade beats Anchor, ' +
            'Anchor beats Pressure, and matching Intents share the result. The winner decides ' +
            'the Clash Range, which is the range BOTH attacks are then judged against -- so an ' +
            'Intent is not a move, it is a bid for where the exchange happens. Pressure is the ' +
            'bid to fight closer: win with it and the range steps one band toward Clinch.',
      sample: 'You choose Pressure, they choose Evade: you win the contest, and an exchange that ' +
              'started at Long is fought at Pocket -- where your hook is Optimal and their teep is not.'
    },
    Anchor: {
      key: 'Anchor', label: 'ANCHOR', shortLabel: 'ANC', icon: ICONS.intentAnchor,
      colorToken: '--mech-chamber',
      trigger: 'Chosen at Commit, revealed with your opponent\'s. Beats Pressure.',
      oneLiner: 'Anchor holds the range where it is, and beats Pressure in the contest.',
      long: 'The three Intents are a closed cycle: Pressure beats Evade, Evade beats Anchor, ' +
            'Anchor beats Pressure, and matching Intents share the result. Anchor is the bid to ' +
            'fight HERE -- win with it and the range does not move at all, which is the strongest ' +
            'answer when your hand is already Optimal where you stand and the weakest when it is ' +
            'not. Holding a range you cannot punch at is a decision too.',
      sample: 'They choose Pressure to drag you into the Clinch; you choose Anchor. You win, the ' +
              'range stays at Pocket, and their Clinch-only elbow resolves one band out of position.'
    },
    Evade: {
      key: 'Evade', label: 'EVADE', shortLabel: 'EVD', icon: ICONS.intentEvade,
      colorToken: '--mech-chamber',
      trigger: 'Chosen at Commit, revealed with your opponent\'s. Beats Anchor.',
      oneLiner: 'Evade opens the distance a band, and beats Anchor in the contest.',
      long: 'The three Intents are a closed cycle: Pressure beats Evade, Evade beats Anchor, ' +
            'Anchor beats Pressure, and matching Intents share the result. Evade is the bid to ' +
            'fight further out: win with it and the range steps one band toward Long. It is the ' +
            'answer to somebody who wants to hold you where they are strong -- and the thing ' +
            'Pressure is waiting for, because a fighter who always backs up is readable.',
      sample: 'They Anchor at Clinch where their knees live; you Evade. You win, the range opens ' +
              'to Pocket, and the knee they committed to lands Compromised.'
    }
  };

  const TABLES = { attackType: ATTACK_TYPES, followThrough: FOLLOW_THROUGH, defenseType: DEFENSE_TYPES,
                   rangeFit: RANGE_FIT, track: TRACK, chamber: CHAMBER, fatigue: FATIGUE,
                   cycle: CYCLE, intent: INTENT };
  // Order matters here and only here: findAnywhere walks it, so a key shared between two tables
  // would resolve to the earlier one. No key is shared today and the suite asserts it.
  // `track` is APPENDED, and deliberately last: it is the newest table and the one most likely to
  // grow, so putting it at the end keeps every existing key resolving exactly where it did.
  // `intent` is APPENDED for the same reason `track` was: it is the newest table, and adding it
  // at the end leaves every existing key resolving exactly where it did. Its three keys
  // (Pressure/Anchor/Evade) collide with nothing -- asserted, not assumed, by the suite's
  // shared-key check.
  const KINDS = ['attackType', 'followThrough', 'defenseType', 'rangeFit', 'track', 'chamber',
                 'fatigue', 'cycle', 'intent'];

  function get(kind, key) {
    const table = TABLES[kind];
    return (table && key && table[key]) || null;
  }

  // ---------------- Resolving a card to its entries ----------------
  //
  // Every lookup is null-safe and null-returning. 16 of 211 cards have no Attack Type at all
  // (feints, Wing Chun trapping, the dirty tricks) and the Improvised Strike has no `v4` block
  // whatsoever -- §4 gives it "no traits, effects, Stamina cost, Opening use, or continuation". A
  // caller that gets null renders nothing, which is the truth; the failure this guards against is
  // a badge reading "undefined".

  function attackTypeOf(card) {
    if (!card) return null;
    // v4.family first because it is the RULESET's category and the one card text is written
    // against. attack.category is the engine's finer field and is what recovers Knee and Elbow,
    // whose family is deliberately null. Same precedence, and the same reason, as attackFamily()
    // in js/ui-resolution.js -- which takes them in the other order because it is picking a SOUND,
    // where the finer answer is the better one.
    const key = (card.v4 && card.v4.family) || (card.attack && card.attack.category) || null;
    return get('attackType', key);
  }

  function followThroughOf(card) {
    if (!card || !card.v4 || !card.v4.continuation) return null;
    return get('followThrough', card.v4.continuation.kind);
  }

  function defenseTypeOf(card) {
    if (!card || !card.v4) return null;
    return get('defenseType', card.v4.defenseFamily);
  }

  // ---------------- Finding a card by what it DOES ----------------
  //
  // Both card lists in the game -- the library's Cards tab and the deck builder's legal pool --
  // searched `name + id + moveGloss`, and moveGloss is FLAVOUR ("Angle the hips in for leverage on
  // the throw."). The printed rules were not searched at all, which is a strange thing to discover
  // about a game whose rules are printed on the cards: a player looking for the Off-Balance cards
  // typed "off-balance" and got ONE result out of the forty-five that say those words, because
  // exactly one card happens to have the phrase in its flavour line. The mechanic looked like a
  // one-card mechanic.
  //
  // It belongs in this file for the reason at the top of it -- one source for what PUNCH, CHAIN and
  // ABSORB mean, everywhere a player can read them. Finding a card by those words is reading them.
  // Both pages load this module already, and two pages searching differently is the same bug in
  // two places.
  //
  // WHAT IS INDEXED, and why each is in or out:
  //
  //   name, id, moveGloss   what was already searched; kept, because "hip" should still find
  //                         Hip Position and because an id is what a bug report quotes.
  //   attack.text,          THE PRINTED RULES, which is the whole point. Both halves: Sapu Sweep
  //   defense.text          pays off Off-Balance on its DEFENCE, and a search that only read the
  //                         attack would miss it.
  //   traits                'Dirty', 'Clinch', 'Sweep' -- the words players use for card families
  //                         that no other field spells out.
  //   Attack/Follow/Defense the dictionary's own three answers for this card, so "grapple",
  //   type labels           "control" and "deny" find cards whose text never says them.
  //
  // NOT v4.attackBadge, deliberately, and it is the one field that looks obviously useful. 74 of
  // those strings describe a Combination Opening as "Punch-only", a rule this engine does not run
  // (js/engine.js, COMBINATION_OPENING_IS_PUNCH_ONLY = false). Indexing generated text nobody edits
  // would let a player search a rule that does not exist and get 74 confident results.
  //
  // NORMALISED ON BOTH SIDES, and that is not a nicety: "off-balance" and "off balance" found
  // different cards, and neither found the same card as the other. Everything that is not a letter
  // or a digit collapses to a space, in the haystack and in the query, so punctuation cannot decide
  // whether a search works.
  const SEARCH_CACHE = Object.create(null);

  function normalizeSearch(text) {
    return String(text == null ? '' : text).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  }

  // Built once per card and kept: the pool is 223 cards and both pages re-filter on every
  // keystroke, which is the measurement the library's bounded render already exists because of.
  function searchText(card) {
    if (!card || !card.id) return '';
    if (SEARCH_CACHE[card.id] !== undefined) return SEARCH_CACHE[card.id];
    const typeLabel = (entry) => (entry && entry.label) || '';
    const parts = [
      card.name, card.id, card.moveGloss,
      card.attack && card.attack.text,
      card.defense && card.defense.text,
      (card.traits || []).join(' '),
      typeLabel(attackTypeOf(card)),
      typeLabel(followThroughOf(card)),
      typeLabel(defenseTypeOf(card))
    ];
    const out = normalizeSearch(parts.filter(Boolean).join(' '));
    SEARCH_CACHE[card.id] = out;
    return out;
  }

  // -> true when the card matches. An empty or punctuation-only query matches everything, which is
  // what an empty search box means.
  function matchesSearch(card, query) {
    const q = normalizeSearch(query);
    // NOT OBSERVABLE, and kept anyway. A mutation pass deleted this line with nothing going red,
    // because indexOf('') is 0 and an empty query therefore matches every card whether this
    // returns early or not. It stays because it is the RULE written down -- an empty box is not a
    // filter -- and because the alternative is a reader deriving that from a JavaScript quirk.
    if (!q) return true;
    return searchText(card).indexOf(q) >= 0;
  }

  // ---------------- The badge ----------------
  //
  // THE `MechanicBadge` THE BRIEF ASKS FOR, and the reason it lives here rather than in
  // js/card-tile.js is the brief's own requirement: "Position, color, abbreviation, and icon must
  // remain stable across surfaces." Four files render badges (the card tile, the commit panels, the
  // resolution beats, the history rail) and a second implementation is how one of them ends up a
  // version behind.
  //
  // `interactive` makes it a button that opens the glossary sheet. Off by default: on the compact
  // card a badge is a label inside a tile that is ITSELF a button, and nesting a button inside a
  // button is invalid HTML that swallows the tile's own click.
  function badgeHtml(entry, opts) {
    if (!entry) return '';
    const o = opts || {};
    const cls = ['mech-badge', 'mech-' + entry.key.toLowerCase()];
    if (o.extraClass) cls.push(o.extraClass);
    // Both labels always render; CSS picks one by width. Doing it in JS would need a resize
    // listener and would be wrong inside a tile that is laid out at several widths on one screen.
    const text = `<span class="mech-badge-full">${escapeHtml(entry.label)}</span>` +
                 `<span class="mech-badge-short">${escapeHtml(entry.shortLabel)}</span>`;
    const icon = o.noIcon ? '' : `<span class="mech-badge-icon">${entry.icon}</span>`;
    // The accessible name is the label plus its one-line rule, so a screen reader gets the
    // definition without the visual tooltip -- the brief requires definitions be reachable by
    // screen reader, and a title attribute alone is not.
    const aria = escapeHtml(entry.label + '. ' + entry.oneLiner);
    if (o.interactive) {
      return `<button type="button" class="${cls.join(' ')} is-interactive" ` +
             `style="--mech:var(${entry.colorToken})" title="${escapeHtml(entry.oneLiner)}" ` +
             `aria-label="${aria}" data-mech-kind="${escapeHtml(o.kind || '')}" ` +
             `data-mech-key="${escapeHtml(entry.key)}" ` +
             `onclick="${escapeHtml(o.onclick || 'MechanicsDictionary.openSheet(this)')}">${icon}${text}</button>`;
    }
    return `<span class="${cls.join(' ')}" style="--mech:var(${entry.colorToken})" ` +
           `title="${escapeHtml(entry.oneLiner)}" aria-label="${aria}">${icon}${text}</span>`;
  }

  // ---------------- Sentences composed from the card's own structured data ----------------
  //
  // See the file header for why these are built rather than read off `attackBadge`/`defenseBadge`.
  // Each one states the TRIGGER and the CONSEQUENCE, which is what the brief asks the commit screen
  // to show before the player confirms.

  const LEVEL_WORD = { High: 'High', Mid: 'Mid', Low: 'Low' };

  // §12's Control table, in the same words js/engine.js's CONTROL_WORD uses. Duplicated deliberately
  // and asserted equal by tests/mechanics-dictionary-test.js: the engine's copy is a log string and
  // this one is player copy, and coupling a UI file to an engine internal to save four lines would
  // be the worse trade.
  const CONTROL_PHRASE = {
    advanceOne: 'end one band closer',
    maintainClinch: 'hold the Clinch',
    setClinch: 'end at Clinch',
    setPocket: 'end at Pocket',
    setLong: 'end at Long',
    holdClash: 'hold the End where the Clash was'
  };

  function attackSentence(card) {
    const cont = card && card.v4 && card.v4.continuation;
    if (!cont) return '';
    const entry = get('followThrough', cont.kind);
    if (!entry) return '';
    if (cont.kind === 'Chain') {
      const lvl = LEVEL_WORD[cont.openingLevel] || cont.openingLevel;
      if (!lvl) return entry.oneLiner;
      // The Opening TYPE is deliberately not named here. It is `Combination` on 74 of 77 Chains and
      // decides nothing under this engine's house rule, so printing it would spend the sentence's
      // one hard word on a distinction the player cannot act on.
      return `On contact, chains to ${lvl} — they are +${cont.bonus || 1} exposed there next exchange.`;
    }
    if (cont.kind === 'ImpactRecovery') {
      const rec = LEVEL_WORD[cont.recoveryOpeningLevel] || cont.recoveryOpeningLevel;
      const imp = cont.impactOnAbsorb || 1;
      return `Costs ${imp} Stamina through an Absorb` +
             (rec ? `, but a Deflect or Dodge leaves you exposed at ${rec}.` : '.');
    }
    if (cont.kind === 'Control') {
      const phrase = CONTROL_PHRASE[cont.contactResult];
      return phrase ? `On contact, claims the range: ${phrase}.`
                    : 'On contact, applies this card\'s printed range result.';
    }
    return entry.oneLiner;
  }

  function defenseSentence(card) {
    const res = card && card.v4 && card.v4.defenseResult;
    const entry = defenseTypeOf(card);
    if (!entry) return '';
    if (!res) return entry.oneLiner;
    // KEPT SHORT DELIBERATELY, and the constraint is measured rather than stylistic: two of these
    // are on screen at once on the Commit stage, and at 360x800 the pair pushed that screen past
    // its one-and-a-half-viewport budget. Every clause here earns its width -- the trigger, the
    // effect, and the number or destination the player will act on. Anything else is in the sheet
    // the sentence opens.
    if (entry.key === 'Absorb') {
      return `Full guard: counts as Contact and recovers ${res.recover || 1} Stamina ` +
             'if the attack cost none.';
    }
    if (entry.key === 'Deflect') {
      return 'Full guard: cancels the follow-through and opens the attacker ' +
             `+${res.bonus || 1} where they aimed.`;
    }
    if (entry.key === 'Deny') {
      const phrase = CONTROL_PHRASE[res.control];
      return 'Full guard: cancels the follow-through' +
             (phrase ? ` and claims the range — ${phrase}.` : '.');
    }
    if (entry.key === 'Dodge') {
      const end = res.end || {};
      let where = '';
      if (end.kind === 'shift') {
        where = `, ending ${end.bands || 1} band ` +
                (end.direction === 'farther' ? 'farther out' : 'closer in');
      } else if (end.kind === 'setRange') {
        where = `, ending at ${end.range}`;
      }
      return `Full guard: the attack becomes a Whiff${where}.`;
    }
    return entry.oneLiner;
  }

  // ---------------- State-aware caveats (the brief's "no possible value" case) ----------------
  //
  // "ABSORB: Recovery will be capped because Stamina is already full." The brief asks for this and
  // it is worth being strict about what qualifies: ONLY facts already on the fighter object at
  // render time. Anything that needs the opponent's card, the Clash range or the damage roll would
  // be a prediction engine, and a prediction that is wrong once teaches the player to ignore it.
  //
  // Recovery is the only mechanic in the four with a knowable-in-advance dead case, which is why
  // this returns a string for Absorb and nothing for the other three.
  const RECOVERY_CAP_PER_EXCHANGE = 2;
  function defenseCaveat(card, fighter) {
    const entry = defenseTypeOf(card);
    if (!entry || entry.key !== 'Absorb' || !fighter) return '';
    if (fighter.stamina != null && fighter.maxStamina != null && fighter.stamina >= fighter.maxStamina) {
      return 'Recovery will be capped — Stamina is already full.';
    }
    if (fighter.stamina != null && fighter.stamina <= 0) {
      return 'No recovery — a fighter at 0 Stamina cannot recover.';
    }
    const used = fighter._recoveredThisExchange || 0;
    if (used >= RECOVERY_CAP_PER_EXCHANGE) {
      return 'Recovery will be capped — already at this exchange\'s recovery limit.';
    }
    return '';
  }

  // ---------------- Resolved results ----------------
  //
  // THE `AftermathResult` FORMATTER. Consumes one normalized item from the adapter in
  // js/ui-resolution.js and returns the three parts a surface needs: a head ("ABSORB -> RECOVER 1"),
  // an optional detail line explaining a zero, and a tone the CSS keys off.
  //
  // THE RULE THIS FUNCTION EXISTS TO KEEP, from the brief: "Always render the type name when its
  // rule is evaluated, even if the applied amount is zero... If an aftermath effect produces no
  // visible meter movement, explicitly state why. Otherwise players conclude that the type label is
  // cosmetic or broken." So there is no path here that returns empty for an evaluated mechanic --
  // `tone: 'zero'` with a reason is the answer, never silence.
  // EVERY REASON A CALLER CAN PASS NEEDS AN ENTRY HERE. The fallback below prints the raw key when
  // one is missing, which put the literal string "noContact" on screen in a resolution beat -- the
  // same failure shape as a template variable nobody supplied, and just as silent: the row rendered,
  // it was the right size, and the word was gibberish. tests/mechanics-dictionary-test.js now walks
  // this table against the reasons js/ui-resolution.js can actually produce.
  const REASON_TEXT = {
    cap: 'already recovered the maximum this exchange',
    downed: 'a fighter at 0 Stamina cannot recover',
    full: 'already at maximum Stamina',
    noStaminaLost: 'the attack cost no Stamina, so there was nothing to recover',
    staminaLost: 'the attack got Stamina through, so Absorb recovers nothing',
    cardText: 'replaced by the card\'s own printed text',
    notFullGuard: 'the guard was not full, so the Defense Type never triggered',
    // The three ways a follow-through evaluates to nothing. All three are ordinary -- most attacks
    // do not connect -- and all three are named rather than left blank, because a follow-through
    // that silently produced no row is what teaches a player the badge is decorative.
    noContact: 'no contact, so the follow-through never started',
    whiff: 'the attack missed, so nothing keyed to contact happened',
    cancelled: 'the defense cancelled it',
    // The two ways Deep Flow's recovery is refused. They differ in one field and mean very
    // different things to the player holding the controller: `damaged` is the core rule working
    // and the answer is to defend better; `smothered` is a card the opponent bought and played and
    // the answer is to not be in the Clinch with them.
    damaged: 'the opponent\'s Attack got through, and Flow recovery needs you unhit',
    smothered: 'a card took the Flow recovery away this exchange'
  };

  function formatResult(item) {
    if (!item || !item.type) return null;
    const kind = item.source === 'defense' ? 'defenseType'
      : item.source === 'followThrough' ? 'followThrough' : 'flowRecovery' === item.source ? 'track'
      : item.source === 'fatigue' ? 'fatigue'
      : 'attackType';
    const entry = get(kind, item.type);
    if (!entry) return null;
    const out = { key: entry.key, label: entry.label, shortLabel: entry.shortLabel,
                  entry, kind, head: entry.label, detail: '', tone: 'applied' };

    // THE END-OF-EXCHANGE TRACK ROW BRINGS ITS OWN WORDS, and has to: both its head and its detail
    // name a FIGHTER ("Nicky was hit this exchange"), and this function has no names — it is a pure
    // function of one item. So js/ui-resolution.js composes them where the names are and this
    // branch passes them through, with REASON_TEXT as the fallback for a caller that supplies a
    // reason and no prose.
    //
    // THE HEAD SAYS "DEEP FLOW", NOT THE ENTRY'S LABEL, and that is the point rather than an
    // oversight: the entry is the track and the row is about one BAND of it. Rendered, the row
    // reads FLOW / DEEP FLOW -> RECOVER 1 — the badge names the mechanic and opens its sheet, the
    // result names the band the fighter was standing in. See mechRowsHtml in js/ui-resolution.js
    // for why that overlap is safe.
    if (item.source === 'flowRecovery') {
      out.head = item.head || (entry.label + ' → ' + String(item.result || 'none').toUpperCase());
      out.detail = item.detail || (item.reason ? (REASON_TEXT[item.reason] || item.reason) : '');
      out.tone = item.tone || (item.activated ? 'applied' : 'zero');
      return out;
    }

    // FATIGUE PASSES ITS WORDS THROUGH for the reason the track row above does: its detail names a
    // FIGHTER and this function has none. It is otherwise the simplest row in the table -- there is
    // no cap to explain, no refusal branch, and no opponent to attribute it to. It never has a zero
    // form either: a reshuffle that did not happen emits nothing at all, so an item existing here
    // means the Stamina was taken.
    if (item.source === 'fatigue') {
      out.head = item.head || (entry.label + ' → −' + (item.applied || 1) + ' STAMINA');
      out.detail = item.detail || '';
      out.tone = item.tone || 'applied';
      return out;
    }

    if (item.result === 'none' || item.result == null) {
      out.head = entry.label;
      out.tone = 'zero';
      out.detail = item.reason ? (REASON_TEXT[item.reason] || item.reason) : '';
      return out;
    }
    if (item.result === 'suppressed') {
      out.head = `${entry.label} → REPLACED`;
      out.tone = 'suppressed';
      out.detail = REASON_TEXT.cardText;
      return out;
    }

    // A resolved amount that did not fully apply is the case the brief singles out. `requested` is
    // what the rule asked for and `applied` is what the ledger allowed; naming both is what keeps a
    // capped recovery from reading as a broken one.
    if (item.result === 'recover') {
      const applied = item.applied != null ? item.applied : item.requested;
      out.head = `${entry.label} → RECOVER ${applied}`;
      if (!applied) {
        out.tone = 'zero';
        out.head = `${entry.label} → RECOVER 0`;
        out.detail = REASON_TEXT[item.reason] || 'no Stamina was recovered';
      } else if (item.requested != null && applied < item.requested) {
        out.tone = 'capped';
        out.detail = `${item.requested} requested, ${applied} allowed — ` +
                     (REASON_TEXT[item.reason] || 'the recovery cap held');
      }
      return out;
    }
    if (item.result === 'opening') {
      const lvl = item.level ? ' ' + item.level : '';
      out.head = `${entry.label} → OPENING${lvl}`;
      out.detail = item.detail || '';
      return out;
    }
    if (item.result === 'chain') {
      out.head = `${entry.label} → CHAIN TO ${String(item.level || '').toUpperCase()}`;
      out.detail = item.detail || '';
      return out;
    }
    if (item.result === 'impact') {
      out.head = `${entry.label} → IMPACT ${item.applied != null ? item.applied : item.requested || 1}`;
      out.detail = item.detail || '';
      return out;
    }
    if (item.result === 'range') {
      out.head = `${entry.label} → ${String(item.detail || 'RANGE CLAIMED').toUpperCase()}`;
      out.detail = '';
      return out;
    }
    if (item.result === 'whiff') {
      out.head = `${entry.label} → WHIFF`;
      out.detail = item.detail || 'nothing keyed to contact happens';
      return out;
    }
    if (item.result === 'cancelled') {
      out.head = `${entry.label} → FOLLOW-THROUGH CANCELLED`;
      out.detail = item.detail || '';
      return out;
    }
    out.head = `${entry.label} → ${String(item.result).toUpperCase()}`;
    out.detail = item.detail || '';
    return out;
  }

  // ---------------- A card's mechanics, spelled out ----------------
  //
  // THE SAME THREE FACTS THE BOUT SHOWS, for a surface with room to explain them: the card detail
  // sheet in library.html and deck-builder.html. The brief's acceptance criterion is that "the same
  // definitions appear in bout, library, deck builder, and glossary surfaces", and this is how the
  // two collection pages meet it without either of them owning a word of the copy.
  //
  // A card comparison in the deck builder is EXACTLY the decision these definitions serve. Choosing
  // between two Defenses is choosing between Absorb's Stamina and Deny's range, and until this
  // existed the builder showed Guard and cost and left the actual difference unstated.
  function cardMechanicsHtml(card) {
    if (!card) return '';
    const rows = [];
    const row = (kindLabel, entry, sentence) => {
      if (!entry) return;
      rows.push(`<div class="mech-card-row">` +
        `<span class="mech-card-kind">${escapeHtml(kindLabel)}</span>` +
        `<span class="mech-card-body">${badgeHtml(entry, { extraClass: 'is-detail' })}` +
        `<span class="mech-card-rule">${escapeHtml(sentence || entry.oneLiner)}</span></span></div>`);
    };
    row('Attack Type', attackTypeOf(card), null);
    row('Follow-through', followThroughOf(card), attackSentence(card));
    row('Defense Type', defenseTypeOf(card), defenseSentence(card));
    // CHAMBER (RULES.md §19), and it belongs here for the reason the three rows above do: this block
    // is where a card explains the SUBSYSTEMS it participates in, and a Chamber card was the only
    // kind that could name one in its rules text and then explain nothing. A player meeting
    // "gain 1 Chamber" for the first time in the Library had no way to find out what that meant
    // without leaving the page.
    //
    // TWO ROWS WHEN A CARD DOES BOTH, because they are two different jobs and Kinetic Recovery does
    // both halves. The badge relabels rather than taking a second glossary entry: loading and
    // spending are one mechanic seen at two moments, so both rows open the same sheet.
    const chamber = get('chamber', 'Chamber');
    if (chamber) {
      const a = card.attack, d = card.defense;
      if (a && a.release) {
        row('Release', Object.assign({}, chamber, { label: 'RELEASE', shortLabel: 'RLS' }),
            'Armed at Commit, before you see their cards: spends ALL your stored Chamber. One is ' +
            'worth +1 Power, two is worth +3. It spends whether it lands or not.');
      }
      if ((a && a.chamber) || (d && d.chamber)) {
        const halves = [];
        if (a && a.chamber) halves.push('Attack');
        if (d && d.chamber) halves.push('Defense');
        row('Chamber', chamber,
            'This card stores Chamber — see its ' + halves.join(' and ') + ' text for when. ' +
            'Max 2, it never expires, and one opponent Attack taking 2 or more Stamina from you ' +
            'clears all of it. A single point does not.');
      }
    }
    if (!rows.length) return '';
    return `<div class="mech-card">${rows.join('')}</div>`;
  }

  // ---------------- The glossary sheet ----------------
  //
  // Reachable in one tap from any badge, in-bout, without navigating away -- the brief's requirement.
  // Rendered through window.UISheet where it exists so the game gets its real dialog semantics
  // (focus trap, escape, backdrop) for free; where it does not -- the deck builder before its script
  // tag was added, or any harness -- the badge keeps its title attribute and this simply does
  // nothing, which is a degraded render rather than a thrown error out of an onclick.
  function sheetBodyHtml(entry) {
    return `<p class="mech-sheet-rule">${escapeHtml(entry.oneLiner)}</p>` +
           `<div class="mech-sheet-row"><span class="mech-sheet-key">When</span>` +
           `<span class="mech-sheet-val">${escapeHtml(entry.trigger)}</span></div>` +
           `<p class="mech-sheet-long">${escapeHtml(entry.long)}</p>` +
           `<p class="mech-sheet-sample"><b>For example:</b> ${escapeHtml(entry.sample)}</p>`;
  }

  function openSheet(elOrKind, maybeKey) {
    let kind = elOrKind, key = maybeKey;
    // Called from an inline onclick with `this`, or directly with (kind, key).
    if (elOrKind && typeof elOrKind === 'object' && elOrKind.getAttribute) {
      kind = elOrKind.getAttribute('data-mech-kind');
      key = elOrKind.getAttribute('data-mech-key');
    }
    const entry = get(kind, key) || findAnywhere(key);
    if (!entry) return false;
    if (window.MechanicsTelemetry && window.MechanicsTelemetry.note) {
      window.MechanicsTelemetry.note('sheetOpened', entry.key);
    }
    if (!window.UISheet || !window.UISheet.open) return false;
    window.UISheet.open({
      title: entry.label.charAt(0) + entry.label.slice(1).toLowerCase(),
      bodyHtml: sheetBodyHtml(entry),
      actions: [{ label: 'Got it', kind: 'primary' }]
    });
    return true;
  }

  // A key with no namespace -- what an inline handler degrades to if the kind attribute is ever
  // dropped. Searched in a fixed order so the answer is stable; no key is shared between tables.
  function findAnywhere(key) {
    for (let i = 0; i < KINDS.length; i++) {
      const hit = get(KINDS[i], key);
      if (hit) return hit;
    }
    return null;
  }

  // Every distinct entry, deduplicated -- the three Authored keys share one object, so a caller
  // building a glossary index gets one row for them rather than three identical ones.
  function all() {
    const seen = [];
    KINDS.forEach(kind => {
      Object.keys(TABLES[kind]).forEach(key => {
        const entry = TABLES[kind][key];
        if (entry && seen.indexOf(entry) < 0) seen.push(entry);
      });
    });
    return seen;
  }

  window.MechanicsDictionary = {
    KINDS, TABLES, CONTROL_PHRASE, REASON_TEXT,
    get, all, findAnywhere,
    attackTypeOf, followThroughOf, defenseTypeOf,
    badgeHtml, iconFor: (entry) => (entry && entry.icon) || '', intentMark,
    // The one search both card lists use. Exported as the pair rather than as the predicate alone
    // because a caller that wants to highlight, rank or explain a match needs the haystack too.
    searchText, matchesSearch, normalizeSearch,
    attackSentence, defenseSentence, defenseCaveat, cardMechanicsHtml,
    formatResult,
    sheetBodyHtml, openSheet
  };
})();
